import React, { createContext, useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const PayrollContext = createContext();
export const usePayroll = () => useContext(PayrollContext);

export const PayrollProvider = ({ children }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Payrolls
  const fetchPayrolls = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      if (!token) return;

      const response = await fetch(`${BASE_URL}/payrolls`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setError("Failed to fetch payrolls");
        return;
      }

      const data = await parseJson(response);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setPayrolls(data.data || []);
      } else {
        setError(data.message || "Failed to fetch payrolls");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Create Payroll
  const createPayroll = async (payrollData) => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      if (!token) {
        setError("No token found");
        return { success: false, message: "No token found" };
      }

      const response = await fetch(`${BASE_URL}/payrolls`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payrollData),
      });

      if (!response.ok) {
        const data = await parseJson(response);

        return {
          success: false,
          message: data?.message || "Failed to create payroll",
        };
      }

      const data = await parseJson(response);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchPayrolls();

        toast.success(data.message || "Payroll created successfully!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create payroll",
      };
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");

      return {
        success: false,
        message: err.message || "Something went wrong",
      };
    } finally {
      setLoading(false);
    }
  };

  // Update Payroll
  const updatePayroll = async (id, payrollData) => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      if (!token) {
        setError("No token found");
        return { success: false, message: "No token found" };
      }

      const response = await fetch(`${BASE_URL}/payrolls/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payrollData),
      });

      if (!response.ok) {
        const data = await parseJson(response);

        return {
          success: false,
          message: data?.message || "Failed to update payroll",
        };
      }

      const data = await parseJson(response);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPayrolls();

        toast.success(data.message || "Payroll updated successfully!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update payroll",
      };
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");

      return {
        success: false,
        message: err.message || "Something went wrong",
      };
    } finally {
      setLoading(false);
    }
  };

  // Delete Payroll
  const deletePayroll = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/payrolls/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete payroll",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchPayrolls();

        toast.success(data.message || "Payroll deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete payroll",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting payroll",
      };
    }
  };

  // Generate Monthly Payroll
 const generateMonthlyPayroll = async ({ year, month }) => {
  setLoading(true);
  setError(null);

  try {
    const token = getToken();

    if (!token) {
      setError("No token found");

      return {
        success: false,
        status: 401,
        message: "No token found",
      };
    }

    const response = await fetch(`${BASE_URL}/payroll/generate-monthly`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        year,
        month,
      }),
    });

    const data = await parseJson(response);

    if (!response.ok) {
      if (response.status === 409) {
        toast.info(
          data?.message || "Payroll has already been generated for this month."
        );
      } else {
        toast.error(data?.message || "Failed to generate payroll");
      }

      return {
        success: false,
        status: response.status,
        message: data?.message || "Failed to generate payroll",
      };
    }

    if (!data) {
      toast.error("Invalid server response");

      return {
        success: false,
        status: response.status,
        message: "Invalid server response",
      };
    }

    if (data.status === 200) {
      await fetchPayrolls();

      toast.success(
        data.message || "Monthly payroll generated successfully!"
      );

      return {
        success: true,
        status: response.status,
        message: data.message,
        data,
      };
    }

    toast.error(data.message || "Failed to generate payroll");

    return {
      success: false,
      status: response.status,
      message: data.message || "Failed to generate payroll",
    };
  } catch (err) {
    console.error(err);
    setError(err.message || "Something went wrong");
    toast.error(err.message || "Something went wrong");

    return {
      success: false,
      status: 500,
      message: err.message || "Something went wrong",
    };
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    const token = getToken();
    if (token) fetchPayrolls();
  }, []);

  return (
    <PayrollContext.Provider
      value={{
        payrolls,
        loading,
        error,
        fetchPayrolls,
        createPayroll,
        updatePayroll,
        deletePayroll,
        generateMonthlyPayroll,
      }}
    >
      {children}
    </PayrollContext.Provider>
  );
};